<template>
  <v-dialog
      v-model="show"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      @keydown.esc="close"
  >
    <v-card class="live-editor-wrapper" tile>
      <!-- HEADER -->
      <v-toolbar color="primary" dark dense elevation="0" class="flex-grow-0">
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          Live Editor - {{ orderName }}
          <span class="text-caption ml-2" style="opacity: 0.7">
            ({{ configMaterial }} / {{ configOrientation }} / {{ configSize }})
          </span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text dark @click="save">
          <v-icon left>mdi-content-save</v-icon> Save Changes
        </v-btn>
      </v-toolbar>

      <!-- BODY -->
      <div class="editor-body">
        <!-- LEFT: CANVAS AREA -->
        <div class="canvas-container" ref="canvasRef">
          <div class="canvas-inner">
            <!-- FRAME -->
            <div
                ref="frameRef"
                class="print-frame elevation-10"
                :class="{ 'print-frame--drag-over': isFrameDragOver }"
                :style="frameContainerStyle"
                @mousedown.prevent="startArtworkDrag"
                @wheel.prevent="onWheel"
                @dragover.prevent="onFrameDragOver"
                @dragenter.prevent="onFrameDragEnter"
                @dragleave.prevent="onFrameDragLeave"
                @drop.prevent="onFrameDrop"
            >
              <!-- Artwork -->
              <img
                  v-if="currentImageUrl"
                  :src="currentImageUrl"
                  class="artwork-layer"
                  :style="artworkImageStyle"
                  @load="onImageLoad"
                  draggable="false"
              />
              <div v-else class="loading-placeholder">
                <v-progress-circular indeterminate color="grey"></v-progress-circular>
              </div>

              <!-- Draggable Texts -->
              <div
                  v-for="(element, idx) in localTextElements"
                  :key="'text-'+idx"
                  class="text-layer"
                  :style="getTextStyle(element, idx)"
                  @mousedown.stop.prevent="startTextDrag($event, idx)"
              >
                {{ element.text }}
              </div>

              <!-- Grid -->
              <div class="grid-lines"></div>
            </div>

            <!-- Floating Controls (unter dem Frame) -->
            <div class="floating-controls elevation-4">
              <div class="d-flex align-center pa-2">
                <v-icon small class="mr-2">mdi-magnify-minus</v-icon>
                <v-slider
                    v-model="zoomProxy"
                    min="1"
                    max="2.5"
                    step="0.01"
                    hide-details
                    dense
                    class="mx-2"
                    @input="onSliderZoom"
                ></v-slider>
                <v-icon small class="ml-2">mdi-magnify-plus</v-icon>
                <span class="ml-3 caption font-weight-bold" style="width: 40px">
                  {{ (localCanvas.scale * 100).toFixed(0) }}%
                </span>
              </div>

              <v-divider class="my-1"></v-divider>

              <div class="d-flex justify-space-between pa-1">
                <v-btn-toggle v-model="selectedVariant" mandatory dense rounded x-small>
                  <v-btn value="ORIGINAL">Artwork</v-btn>
                  <v-btn
                      v-if="effectiveCustomArtwork"
                      value="CUSTOM"
                  >
                    Custom
                  </v-btn>
                  <v-btn
                      v-for="(url, key) in validUpscales"
                      :key="key"
                      :value="key"
                  >
                    {{ mapVariantName(key) }}
                  </v-btn>
                </v-btn-toggle>
                <v-btn @click="downloadArtwork" icon><v-icon>mdi-download</v-icon></v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: SIDEBAR -->
        <div class="settings-sidebar">
          <div class="pa-4 sidebar-inner">
            <!-- PREVIEWS -->
            <div v-if="task.revisionComment" class="text-subtitle-1 font-weight-bold mb-2">Revision Comment</div>
            <div class="preview-box preview-box--warning mb-2">
              <span>{{task.revisionComment}}</span>
            </div>
            <div class="text-subtitle-1 font-weight-bold mb-2">Original Customer Preview</div>
            <div class="preview-box mb-2">
              <img @click="() => dialogImage = currentImageUrl" :src="customizationData.previewImg" class="preview-img preview-img-main" />
            </div>
            <div
                @click="() => dialogImage = customizationData.originalImage"
                v-if="customizationData.originalImage"
                class="preview-box preview-box-small mb-4"
            >
              <div class="text-caption grey--text mb-1">Original Upload</div>
              <img :src="customizationData.originalImage" class="preview-img preview-img-small" />
            </div>

            <v-divider class="mb-4"></v-divider>

            <!-- TEXT SETTINGS -->
            <div class="d-flex justify-space-between align-center mb-2">
              <div class="text-subtitle-1 font-weight-bold">Texts</div>
              <div>
                <v-btn icon small @click="deleteTexts">
                  <v-icon>mdi-delete-forever</v-icon>
                </v-btn>
                <v-btn icon small @click="resetTexts">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>
            </div>

            <v-expansion-panels accordion multiple v-model="openPanels" flat>
              <v-expansion-panel
                  v-for="(element, index) in localTextElements"
                  :key="index"
                  class="grey lighten-5 mb-2 rounded border-panel"
              >
                <v-expansion-panel-header class="py-2">
                  Text #{{ index + 1 }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field
                      v-model="element.text"
                      label="Content"
                      outlined
                      dense
                      hide-details
                      class="mb-3 white"
                  ></v-text-field>

                  <v-row dense>
                    <v-col cols="8">
                      <v-select
                          v-model="element.font"
                          :items="fontOptions"
                          label="Font"
                          outlined
                          dense
                          hide-details
                          class="white"
                          :menu-props="{ bottom: true, offsetY: true }"
                      >
                        <template v-slot:item="{ item }">
                          <span :style="{ fontFamily: item }">{{ item }}</span>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="4">
                      <div
                          class="color-swatch"
                          :style="{ backgroundColor: element.color }"
                          @click="openColorPicker(index)"
                      >
                        <input
                            type="color"
                            v-model="element.color"
                            :ref="'cp'+index"
                            class="hidden-input"
                        >
                      </div>
                    </v-col>
                  </v-row>

                  <div class="mt-4">
                    <div class="caption grey--text">Size ({{ element.size }}%)</div>
                    <v-slider
                        v-model="element.size"
                        min="1"
                        max="20"
                        step="0.1"
                        dense
                        hide-details
                    ></v-slider>
                  </div>

                  <div class="mt-2">
                    <div class="caption grey--text">Pos Y ({{ element.y }}%)</div>
                    <v-slider
                        v-model="element.y"
                        min="0"
                        max="100"
                        step="0.5"
                        dense
                        hide-details
                    ></v-slider>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SEND TO FULFILL -->
            <v-divider class="mt-4 mb-3"></v-divider>

            <div class="live-action-container">
              <v-btn
                  v-if="!isRatingView"
                  block
                  class="mt-2"
                  color="warning"
                  :loading="isSending"
                  :disabled="isSending || !currentImageUrl"
                  @click="openGeminiDialog"
              >
                <v-icon left>mdi-alert</v-icon>
                Überarbeitung Nötig
              </v-btn>
              <div v-if="!isRatingView" class="text-caption grey--text mt-1">
                Details müssen überarbeitet werden
              </div>
              <v-divider class="mt-4 mb-3"></v-divider>
              <v-btn
                  block
                  class="mt-2"
                  color="primary"
                  :loading="isSending"
                  :disabled="isSending || !currentImageUrl"
                  @click="() => sendToFulfill(true)"
              >
                <v-icon left>mdi-truck-check</v-icon>
                <v-icon left>mdi-mail</v-icon>
                Preview + Fulfill
              </v-btn>
              <div class="text-caption grey--text mt-1">
                E-Mail - 24h Frist
              </div>
              <v-btn
                  v-if="!isRatingView"
                  block
                  color="success"
                  class="mt-2"
                  :loading="isSending"
                  :disabled="isSending || !currentImageUrl"
                  @click="() => sendToFulfill(false)"
              >
                <v-icon left>mdi-truck-check</v-icon>
                Send To Fulfill
              </v-btn>
              <div v-if="!isRatingView" class="text-caption grey--text mt-1">
                Geht direkt in den Fulfill
              </div>
              <div
                  v-if="sendError"
                  class="mt-2 red--text text-caption"
              >
                {{ sendError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Bild-Vorschau -->
    <ImageDialog
        v-if="dialogImage"
        :show="!!dialogImage"
        :image="dialogImage"
        :onClose="() => dialogImage = null"
    ></ImageDialog>

    <!-- Überarbeitung-Dialog -->
    <v-dialog
        v-model="showGeminiDialog"
        max-width="600px"
        persistent
    >
      <v-card>
        <v-card-title class="headline">
          Überarbeitung nötig
        </v-card-title>
        <v-card-text>
          <v-textarea
              v-model="revisionComment"
              label="Hinweise zur Überarbeitung"
              outlined
              rows="4"
              auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showGeminiDialog = false">
            Abbrechen
          </v-btn>
          <v-btn color="primary" @click="confirmRevision">
            Bestätigen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import {getDownloadURL, getStorage, ref, uploadBytes} from "firebase/storage";
import ImageDialog from "@/components/ImageDialog.vue";
import {downloadImageFromUrl} from "@/utils";
import {mapActions} from "vuex";

export default {
  name: 'LiveEditorDialog',
  components: {ImageDialog},
  props: {
    value: Boolean,
    task: Object,
    isRatingView: Boolean,
    customizationData: {
      type: Object,
      required: true
    },
    orderName: String,
    upscaledUrls: {
      type: Object,
      default: () => ({})
    },
    customArtwork: {
      type: String,
      default: null
    },
    /**
     * Wird nach erfolgreichem Upload mit der finalen URL aufgerufen.
     * onApprove(url: string): void
     */
    onApprove: {
      type: Function,
      default: null
    },
    /**
     * Optional: eigene Uploadfunktion
     * storageUploadFn(blob, path) => Promise<string(url)>
     */
    storageUploadFn: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      showGeminiDialog: false,
      dialogImage: null,

      // lokales Custom Artwork (z.B. via Drag & Drop)
      localCustomArtwork: null,
      isFrameDragOver: false,

      // Kommentar für Überarbeitung
      revisionComment: '',

      // Artwork Canvas State (x,y,scale 1:1 wie im Shop)
      localCanvas: { x: 0, y: 0, scale: 1 },
      localTextElements: [],

      selectedVariant: 'ORIGINAL',
      // Text Panels standardmäßig zu:
      openPanels: [],

      // Größen
      canvasRect: { width: 0, height: 0 },
      frameRect: { width: 0, height: 0 },
      imageAspectRatio: 1,

      // Dragging
      dragMode: null, // null | 'artwork' | 'text'
      dragStart: { x: 0, y: 0 },
      dragStartCanvas: { x: 0, y: 0 },
      dragTextIndex: null,
      dragStartTextPos: { x: 0, y: 0 },

      zoomProxy: 1,

      isSending: false,
      sendError: null,

      fontOptions: [
        'WindSong', 'Playfair Display', 'Montserrat', 'Raleway',
        'Dancing Script', 'Anton', 'Cinzel', 'Permanent Marker'
      ]
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },

    // Effektives Custom-Bild (Prop + lokal via Drag & Drop)
    effectiveCustomArtwork () {
      return this.localCustomArtwork || this.customArtwork || null
    },

    config () {
      return this.customizationData.configuration || {}
    },
    configMaterial () {
      return this.config.material || 'Poster'
    },
    configOrientation () {
      return this.config.orientation || 'portrait'
    },
    configSize () {
      return this.config.size || 'M'
    },

    validUpscales () {
      const valid = {}
      for (const [key, val] of Object.entries(this.upscaledUrls || {})) {
        const url = (typeof val === 'object' && val && val.url) ? val.url : val
        if (url && typeof url === 'string' && url.startsWith('http')) {
          valid[key] = url
        }
      }
      return valid
    },

    currentImageUrl () {
      if (this.selectedVariant === 'CUSTOM') {
        return this.effectiveCustomArtwork
      }
      if (this.selectedVariant === 'ORIGINAL') {
        return this.customizationData.artworkImageUrl || this.customizationData.originalImage
      }
      const val = this.upscaledUrls[this.selectedVariant]
      return (typeof val === 'object' && val) ? val.url : val
    },

    targetAspectRatio () {
      return this.configOrientation === 'landscape' ? 3 / 2 : 2 / 3
    },

    // Frame max. 90% des canvas-container, Höhe aus Ratio
    frameContainerStyle () {
      const ratio = this.targetAspectRatio
      const cw = this.canvasRect.width
      const ch = this.canvasRect.height

      if (!cw || !ch) {
        // Fallback
        const h = 70
        const w = h * ratio
        return {
          height: `${h}vh`,
          width: `${w}vh`
        }
      }

      const maxW = cw * 0.8
      const maxH = ch * 0.8

      let w = maxW
      let h = w / ratio

      if (h > maxH) {
        h = maxH
        w = h * ratio
      }

      return {
        width: `${w}px`,
        height: `${h}px`
      }
    },

    /**
     * imageLayout: Port der React-Logik
     *  baseW/baseH: Bildgröße bei scale=1
     *  currentW/H: base * scale
     *  translateX/Y: x/y * currentW/H
     */
    imageLayout () {
      if (!this.frameRect.width || !this.frameRect.height || !this.imageAspectRatio) {
        return null
      }

      const frameW = this.frameRect.width
      const frameH = this.frameRect.height
      const frameRatio = frameW / frameH
      const imgRatio = this.imageAspectRatio

      let baseW, baseH

      if (imgRatio > frameRatio) {
        baseH = frameH
        baseW = baseH * imgRatio
      } else {
        baseW = frameW
        baseH = baseW / imgRatio
      }

      const currentW = baseW * this.localCanvas.scale
      const currentH = baseH * this.localCanvas.scale

      const translateX = this.localCanvas.x * currentW
      const translateY = this.localCanvas.y * currentH

      return {
        baseW,
        baseH,
        currentW,
        currentH,
        translateX,
        translateY
      }
    },

    artworkImageStyle () {
      const layout = this.imageLayout

      const style = {
        position: 'absolute',
        top: '0',
        left: '0',
        transformOrigin: '0 0',
        willChange: 'transform',
        pointerEvents: 'none',
        userSelect: 'none'
      }

      if (!layout) {
        style.width = '100%'
        style.height = '100%'
        style.transform = 'none'
        return style
      }

      style.width = `${layout.baseW}px`
      style.height = `${layout.baseH}px`
      style.transform = `translate(${layout.translateX}px, ${layout.translateY}px) scale(${this.localCanvas.scale})`

      return style
    },
  },
  watch: {
    value (val) {
      if (val) {
        this.initData()
        this.$nextTick(() => {
          this.updateMeasurements()
        })
      }
    }
  },
  created () {
    this.initData()
    this.measureFrame()
    // kleiner Delay wie im React Modal wegen Animation
    setTimeout(() => this.measureFrame(), 100)
  },
  mounted () {
    this.updateMeasurements()
    window.addEventListener('resize', this.onResize)
    window.addEventListener('mouseup', this.stopDrag)
    window.addEventListener('mousemove', this.onDrag)

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('mouseup', this.stopDrag)
    window.removeEventListener('mousemove', this.onDrag)

    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);

    // aufgeräumtes Revoke von blob:-URLs
    if (this.localCustomArtwork && typeof this.localCustomArtwork === 'string' && this.localCustomArtwork.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(this.localCustomArtwork)
      } catch (e) {
        // ignore
      }
    }
  },
  methods: {
    ...mapActions(['markTask']),
    // --- INIT & MESSEN ---
    onKeyDown(e) {
      if (this.show && (e.key === "y" || e.key === "Y")) {
        if (this.dialogImage !== this.customizationData.originalImage) {
          this.dialogImage = this.currentImageUrl;
        }
      }
    },

    onKeyUp(e) {
      if (this.show &&  (e.key === "y" || e.key === "Y")) {
        if (this.dialogImage !== this.customizationData.originalImage) {
          this.dialogImage = null;
        }
      }
    },

    openGeminiDialog() {
      this.revisionComment = ''
      this.showGeminiDialog = true
    },

    confirmRevision() {
      this.markTask({uid: this.task.uid, revisionComment: this.revisionComment});
      this.showGeminiDialog = false
    },

    initData () {
      const config = this.customizationData.configuration || {}

      this.localCanvas = config.artworkCanvas
          ? { ...config.artworkCanvas }
          : { x: 0, y: 0, scale: 1 }

      this.zoomProxy = this.localCanvas.scale || 1

      this.localTextElements = config.textElements
          ? JSON.parse(JSON.stringify(config.textElements))
          : []

      // lokales Custom Artwork beim Öffnen zurücksetzen
      this.localCustomArtwork = null

      if (this.effectiveCustomArtwork) {
        this.selectedVariant = 'CUSTOM'
      } else if (this.upscaledUrls['DEFAULT'] && this.upscaledUrls['DEFAULT'].url) {
        this.selectedVariant = 'DEFAULT'
      } else {
        this.selectedVariant = 'ORIGINAL'
      }

      this.openPanels = [] // standardmäßig geschlossen
    },

    updateMeasurements () {
      this.measureCanvas()
      this.$nextTick(() => {
        this.measureFrame()
      })
    },

    async downloadArtwork() {
      await downloadImageFromUrl(this.currentImageUrl, this.orderName, () => {}, 'jpg');
    },

    measureCanvas () {
      if (this.$refs.canvasRef) {
        const rect = this.$refs.canvasRef.getBoundingClientRect()
        this.canvasRect = { width: rect.width, height: rect.height }
      }
    },

    measureFrame () {
      if (this.$refs.frameRef) {
        const rect = this.$refs.frameRef.getBoundingClientRect()
        this.frameRect = { width: rect.width, height: rect.height }
      }
    },

    onResize () {
      this.updateMeasurements()
    },

    onImageLoad (e) {
      const { naturalWidth, naturalHeight } = e.target
      if (naturalHeight > 0) {
        this.imageAspectRatio = naturalWidth / naturalHeight
      }
      this.$nextTick(() => {
        this.updateMeasurements()
        this.$nextTick(() => this.clampCanvas())
      })
    },

    mapVariantName (key) {
      const map = { DEFAULT: 'Default', PRO_CAR: 'Car', PRO_PERSON: 'Person' }
      return map[key] || key
    },

    // --- TEXT STYLE & DRAG ---
    getTextStyle (text, idx) {
      const frameW = this.frameRect.width || 500
      const sizePx = (frameW * text.size) / 100

      return {
        position: 'absolute',
        left: `${text.x}%`,
        top: `${text.y}%`,
        transform: 'translate(-50%, -50%)',
        fontFamily: text.font,
        fontSize: `${sizePx}px`,
        color: text.color,
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        cursor: 'move',
        pointerEvents: 'auto',
        userSelect: 'none'
      }
    },

    resetTexts () {
      const config = this.customizationData.configuration || {}
      this.localTextElements = JSON.parse(JSON.stringify(config.textElements || []))
    },

    deleteTexts () {
      this.localTextElements = this.localTextElements.map(tEl => {
        return  {...tEl, text: ''}
      })
    },

    openColorPicker (index) {
      const ref = this.$refs['cp' + index]
      if (ref && typeof ref.click === 'function') {
        ref.click()
      }
    },

    // --- DRAG ARTWORK ---
    startArtworkDrag (e) {
      if (!this.imageLayout) return
      if (this.dragMode === 'text') return
      this.dragMode = 'artwork'
      this.dragStart = { x: e.clientX, y: e.clientY }
      this.dragStartCanvas = { ...this.localCanvas }
    },

    dragArtwork (e) {
      if (this.dragMode !== 'artwork' || !this.imageLayout) return

      const layout = this.imageLayout
      const dxPx = e.clientX - this.dragStart.x
      const dyPx = e.clientY - this.dragStart.y

      const dXPct = dxPx / layout.currentW
      const dYPct = dyPx / layout.currentH

      this.localCanvas.x = this.dragStartCanvas.x + dXPct
      this.localCanvas.y = this.dragStartCanvas.y + dYPct

      this.clampCanvas()
    },

    // --- DRAG TEXT ---
    startTextDrag (e, index) {
      if (!this.frameRect.width || !this.localTextElements[index]) return
      this.dragMode = 'text'
      this.dragTextIndex = index
      this.dragStart = { x: e.clientX, y: e.clientY }
      this.dragStartTextPos = {
        x: this.localTextElements[index].x,
        y: this.localTextElements[index].y
      }
    },

    dragText (e) {
      if (this.dragMode !== 'text') return
      const idx = this.dragTextIndex
      if (idx == null || !this.localTextElements[idx]) return

      const dxPx = e.clientX - this.dragStart.x
      const dyPx = e.clientY - this.dragStart.y

      const frameW = this.frameRect.width || 1
      const frameH = this.frameRect.height || 1

      const dXPct = (dxPx / frameW) * 100
      const dYPct = (dyPx / frameH) * 100

      const el = this.localTextElements[idx]
      let newX = this.dragStartTextPos.x + dXPct
      let newY = this.dragStartTextPos.y + dYPct

      // Snap zur Mitte (50%) – +/- 2% Toleranz
      const SNAP_THRESHOLD = 2
      if (Math.abs(newX - 50) < SNAP_THRESHOLD) {
        newX = 50
      }
      if (Math.abs(newY - 50) < SNAP_THRESHOLD) {
        newY = 50
      }

      el.x = newX
      el.y = newY
    },

    onDrag (e) {
      if (!this.dragMode) return
      if (this.dragMode === 'artwork') {
        this.dragArtwork(e)
      } else if (this.dragMode === 'text') {
        this.dragText(e)
      }
    },

    stopDrag () {
      this.dragMode = null
      this.dragTextIndex = null
    },

    // --- DRAG & DROP CUSTOM IMAGE ---
    onFrameDragOver (e) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy'
      }
    },
    onFrameDragEnter () {
      this.isFrameDragOver = true
    },
    onFrameDragLeave (e) {
      // nur wirklich verlassen, wenn wir den Frame verlassen
      if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return
      this.isFrameDragOver = false
    },
    onFrameDrop (e) {
      this.isFrameDragOver = false
      const dt = e.dataTransfer
      if (!dt) return

      // 1) Versuche zuerst Dateiupload (z.B. vom Desktop)
      if (dt.files && dt.files.length) {
        const file = Array.from(dt.files).find(f => f.type && f.type.startsWith('image/'))
        if (file) {
          const objectUrl = URL.createObjectURL(file)
          this.applyCustomImage(objectUrl)
          return
        }
      }

      // 2) Fallback: URL-Drop (z.B. aus dem Browser)
      if (dt.items && dt.items.length) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i]
          if (item.kind === 'string' && item.type === 'text/uri-list') {
            item.getAsString(url => {
              if (url) {
                this.applyCustomImage(url.trim())
              }
            })
            return
          }
        }
      }
    },
    applyCustomImage (url) {
      // altes blob-URL aufräumen
      if (this.localCustomArtwork && typeof this.localCustomArtwork === 'string' && this.localCustomArtwork.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.localCustomArtwork)
        } catch (e) {
          // ignore
        }
      }
      this.localCustomArtwork = url
      this.selectedVariant = 'CUSTOM'
    },

    // --- CLAMP ARTWORK wie im React ArtworkEditor ---
    clampCanvas () {
      const layout = this.imageLayout
      if (!layout || !this.frameRect.width || !this.frameRect.height) return

      const frameW = this.frameRect.width
      const frameH = this.frameRect.height

      const currentW = layout.currentW
      const currentH = layout.currentH

      const minX = frameW - currentW
      const maxX = 0
      const minY = frameH - currentH
      const maxY = 0

      const minXPct = minX / currentW
      const maxXPct = maxX / currentW
      const minYPct = minY / currentH
      const maxYPct = maxY / currentH

      let x = this.localCanvas.x
      let y = this.localCanvas.y

      if (x < minXPct) x = minXPct
      if (x > maxXPct) x = maxXPct
      if (y < minYPct) y = minYPct
      if (y > maxYPct) y = maxYPct

      this.localCanvas.x = x
      this.localCanvas.y = y
    },

    // --- ZOOM ---
    handleZoom (newScale, oldScale) {
      if (!this.frameRect.width || !this.frameRect.height || !this.imageAspectRatio) return
      if (newScale === oldScale) return

      const frameW = this.frameRect.width
      const frameH = this.frameRect.height

      const frameRatio = frameW / frameH
      const imgRatio = this.imageAspectRatio
      let baseW, baseH

      if (imgRatio > frameRatio) {
        baseH = frameH
        baseW = baseH * imgRatio
      } else {
        baseW = frameW
        baseH = baseW / imgRatio
      }

      const currentW = baseW * oldScale
      const currentH = baseH * oldScale

      const ratio = newScale / oldScale

      const currentPixelX = this.localCanvas.x * currentW
      const currentPixelY = this.localCanvas.y * currentH

      const newPixelX = currentPixelX * ratio + (frameW / 2) * (1 - ratio)
      const newPixelY = currentPixelY * ratio + (frameH / 2) * (1 - ratio)

      const newW = currentW * ratio
      const newH = currentH * ratio

      const newX = newPixelX / newW
      const newY = newPixelY / newH

      this.localCanvas = {
        ...this.localCanvas,
        x: newX,
        y: newY,
        scale: newScale
      }

      this.$nextTick(() => {
        this.clampCanvas()
        this.zoomProxy = this.localCanvas.scale
      })
    },

    onSliderZoom (value) {
      const newScale = Math.min(Math.max(parseFloat(value), 1), 2.5)
      const oldScale = this.localCanvas.scale
      this.handleZoom(newScale, oldScale)
    },

    onWheel (e) {
      const delta = e.deltaY > 0 ? -0.05 : 0.05
      let next = this.localCanvas.scale + delta
      next = Math.min(Math.max(next, 1), 2.5)
      this.handleZoom(next, this.localCanvas.scale)
    },

    // --- UTILS für Canvas & Upload ---
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image()

        // crossOrigin nur für http/https
        if (typeof url === 'string' && url.startsWith('http')) {
          img.crossOrigin = 'anonymous'
        }

        img.onload = () => resolve(img)
        img.onerror = (e) => {
          console.error("Image load error", e);
          reject(e);
        }
        img.src = url
      })
    },

    canvasToBlob (canvas, type = 'image/jpeg', quality = 0.9) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (blob) resolve(blob)
          else reject(new Error('Failed to create blob from canvas'))
        }, type, quality)
      })
    },

    generateShortId () {
      return Math.random().toString(36).substr(2, 6)
    },

    async uploadToStorage (blob, path) {
      const storage = getStorage();
      const storageRef = ref(storage, path);

      // Upload the blob to Firebase Storage
      const uploadResponse = await uploadBytes(storageRef, blob);

      // Get the download URL
      return await getDownloadURL(uploadResponse.ref);
    },

    renderArtworkToCanvas (ctx, img, frameW, frameH) {
      const imgW = img.naturalWidth
      const imgH = img.naturalHeight
      if (!imgW || !imgH) return

      const imgRatio = imgW / imgH
      const frameRatio = frameW / frameH

      let baseScale
      if (imgRatio > frameRatio) {
        // Höhe anpassen
        baseScale = frameH / imgH
      } else {
        // Breite anpassen
        baseScale = frameW / imgW
      }

      const scale = this.localCanvas.scale || 1
      const finalScale = baseScale * scale

      const scaledW = imgW * finalScale
      const scaledH = imgH * finalScale

      const offsetX = this.localCanvas.x * scaledW
      const offsetY = this.localCanvas.y * scaledH

      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.scale(finalScale, finalScale)
      ctx.drawImage(img, 0, 0)
      ctx.restore()
    },

    renderTextsToCanvas (ctx, frameW, frameH) {
      if (!Array.isArray(this.localTextElements)) return

      this.localTextElements.forEach(el => {
        if (!el || !el.text) return

        const fontSizePx = (frameW * el.size) / 100
        const x = (el.x / 100) * frameW
        const y = (el.y / 100) * frameH

        ctx.save()
        ctx.font = `${fontSizePx}px ${el.font || 'sans-serif'}`
        ctx.fillStyle = el.color || '#000000'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(el.text, x, y)
        ctx.restore()
      })
    },

    // SEND TO FULFILL
    async sendToFulfill (sendPreviewCustomer = false) {
      try {
        this.isSending = true
        this.sendError = null

        this.$emit('approveProcessed')

        function extractSize(str) {
          const lastPart = str.split('x')?.[0]?.trim();
          const match = lastPart.match(/^(\d+)/);
          return match ? Number(match[1]) : null;
        }

        const size = extractSize(this.customizationData.configuration.size)
        console.log(size)
        const maxSize = size > 45 ? 8000 : 4000

        const orientation = this.configOrientation || 'portrait'
        const ratio = orientation === 'landscape' ? 3 / 2 : 2 / 3

        let width, height
        if (orientation === 'landscape') {
          width = maxSize
          height = Math.round(width / ratio)
        } else {
          height = maxSize
          width = Math.round(height * ratio)
        }

        const imgUrl =
            this.currentImageUrl ||
            this.customizationData.artworkImageUrl ||
            this.customizationData.originalImage

        if (!imgUrl) {
          throw new Error('No artwork image URL available.')
        }

        const img = await this.loadImage(imgUrl)

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')

        // Hintergrund weiß
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, width, height)

        // Artwork rendern
        this.renderArtworkToCanvas(ctx, img, width, height)

        // Texte rendern
        this.renderTextsToCanvas(ctx, width, height)

        const blob = await this.canvasToBlob(canvas, 'image/jpeg', 0.9)

        const shortId = this.generateShortId()
        const baseName = (this.orderName || 'order').toString().trim()
        const safeOrderName = baseName
            .replace(/\s+/g, '-')
            .replace(/[^a-zA-Z0-9-_]/g, '')
            .toLowerCase() || 'order'
        const fileName = `${safeOrderName}-${shortId}.jpg`
        const path = `templates/11_2025/final/${fileName}`

        const url = await this.uploadToStorage(blob, path)

        this.$emit('approve', {
          finalUrl: url,
          sendPreviewCustomer: sendPreviewCustomer
        })

        this.$emit('approved', url)
      } catch (err) {
        console.error(err)
        this.sendError = err && err.message ? err.message : String(err)
      } finally {
        this.isSending = false
      }
    },

    // --- SAVE / CLOSE ---
    save () {
      this.$emit('save', {
        id: this.customizationData.id,
        configuration: {
          ...this.config,
          artworkCanvas: { ...this.localCanvas },
          textElements: this.localTextElements
        },
        selectedUpscaleVariant: this.selectedVariant
      })
      this.close()
    },
    close () {
      this.stopDrag()
      this.showGeminiDialog = false
      this.isFrameDragOver = false
      this.show = false
    }
  }
}
</script>

<style scoped>
.live-action-container {
  bottom: 0;
  margin-bottom: 12px;
  text-align: center;
}

.live-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
}

.editor-body {
  display: flex;
  flex: 1;
  height: calc(100% - 48px); /* Toolbar height */
  overflow: hidden;
}

/* LEFT SIDE */
.canvas-container {
  flex: 1;
  background-color: #333;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.canvas-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.print-frame {
  background-color: white;
  position: relative;
  overflow: hidden;
  cursor: grab;
  flex: 0 0 auto;
}

.print-frame:active {
  cursor: grabbing;
}

/* visuelles Feedback bei Drag & Drop */
.print-frame--drag-over {
  outline: 2px dashed rgba(25, 118, 210, 0.9);
  outline-offset: -4px;
}

.artwork-layer {
  display: block;
}

/* Grid overlay */
.grid-lines {
  display: none;
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-image:
      linear-gradient(to right, rgba(0, 0, 0, 1) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(0, 0, 0, 1) 1px, transparent 1px);
  background-size: 33.3% 33.3%;
  z-index: 10;
}

/* Floating controls unter dem Frame */
.floating-controls {
  margin-top: 16px;
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  z-index: 100;
}

/* RIGHT SIDE */
.settings-sidebar {
  width: 360px;
  background: white;
  border-left: 1px solid #ddd;
  overflow-y: auto;
  z-index: 20;
}

.sidebar-inner {
  min-height: 100%;
  position: relative;
}

.preview-box {
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.preview-box--warning {
  background-color: #ffd88d;
}

.preview-img {
  max-width: 100%;
  object-fit: contain;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.preview-img-main {
  max-height: 260px;
}

.preview-box-small {
  background-color: #fafafa;
}

.preview-img-small {
  max-height: 90px;
  box-shadow: none;
}

/* Color picker */
.color-swatch {
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  position: relative;
}

.hidden-input {
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.border-panel {
  border: 1px solid #eee !important;
}

/* Text layer */
.text-layer {
  z-index: 20;
}
</style>
